import React, { useRef } from "react"
import { StaticImage } from "gatsby-plugin-image"
import {
  Layout,
  Seo,
  Cta,
  OverflowAlternatingFeatures,
  BookADemoButton,
  Nav,
  GridList,
} from "../../components"
import Icon from "../../components/icon"
import {
  pageTitle,
  pageMetadata,
  title,
  subtitle,
  description,
  features,
} from "../../data/sensors"
import { useOnScreen } from "../../utils/hooks"

export default function Sensors() {
  const ref = useRef()
  const isHeaderVisible = useOnScreen(ref)
  return (
    <Layout>
      <Seo title={pageTitle} description={pageMetadata} />
      <div>
        <Nav isHeaderVisible={isHeaderVisible} />
        {/* Custom inner header */}
        <div className="bg-gray-900" ref={ref}>
          <div className="max-w-2xl mx-auto py-24 px-4 grid items-center grid-cols-1 gap-y-16 gap-x-8 sm:px-6 sm:py-32 lg:max-w-7xl lg:px-8 lg:grid-cols-2">
            <div>
              <h1>
                <span className="mt-1 font-extrabold block">
                  <span className="block text-white">{title}</span>
                  <span className="block text-pushfusion">{subtitle}</span>
                </span>
              </h1>
              <p className="mt-12 text-base text-gray-300 mr-0 sm:mr-6 sm:mt-5 lg:text-lg xl:text-xl text-justify">
                <div className="sm:-mb-6">
                  {description.map(item => {
                    if (item.bold) {
                      return <h3 className="text-gray-50">{item.text}</h3>
                    } else {
                      return <p className="pb-6">{item.text}</p>
                    }
                  })}
                </div>
              </p>

              <div className="w-full max-w-lg lg:ml-0">
                <div className="mt-10 sm:flex justify-start">
                  <BookADemoButton />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 grid-rows-2 gap-4 sm:gap-6 lg:gap-8">
              <StaticImage
                src="../../assets/images/sensors/air-quality-monitor.png"
                alt="Image of pushfusion interface on a mac book showing some Casambi devices."
                placeholder="blurred"
                layout="constrained"
                quality={100}
                height={480}
                loading="eager"
                aspectRatio={16 / 9}
                className="rounded-lg top-0 lg:top-16 p-28"
              />
              <StaticImage
                src="../../assets/images/sensors/air-quality-plant.png"
                alt="Image of pushfusion interface on a mac book showing some Casambi devices."
                placeholder="blurred"
                layout="constrained"
                quality={100}
                height={480}
                loading="eager"
                aspectRatio={16 / 9}
                className="rounded-lg"
              />

              <StaticImage
                src="../../assets/images/sensors/air-quality-health-issues.png"
                alt="Image of pushfusion interface on a mac book showing some Casambi devices."
                placeholder="blurred"
                layout="constrained"
                quality={100}
                height={480}
                loading="eager"
                aspectRatio={16 / 9}
                className="rounded-lg top-0 lg:top-16 p-28"
              />

              <StaticImage
                src="../../assets/images/sensors/simulation-viruses-workplace.png"
                alt="Image of pushfusion interface on a mac book showing some Casambi devices."
                placeholder="blurred"
                layout="constrained"
                quality={100}
                height={480}
                loading="eager"
                aspectRatio={16 / 9}
                className="rounded-lg"
              />
            </div>
          </div>
        </div>

        <GridList
          title={{
            firstSentence: "What we",
            secondSentence: "measure",
          }}
          description={
            <div className="text-justify">
              Fundamental to the flexibility of{" "}
              <span className="text-gray-900 font-bold">push</span>
              <span className="text-pushfusion font-extrabold">fusion</span> is
              our manufacturer agnostic approach. It means we can tailor the
              system to your preferred hardware suppliers and allows us to pull
              data from other third-party hardware and systems such as emergency
              lighting or air quality monitoring solutions. Users may then
              analyse the data in conjunction with the data retrieved from other
              API's to enjoy a rich reporting environment and a fine-grained
              view of their estate from multiple aspects.
            </div>
          }
          position="start"
          features={features}
          backgroundColor="bg-gray-50"
        />
      </div>

      <OverflowAlternatingFeatures
        title={{ firstSentence: "pushfusion", secondSentence: "dashboard" }}
        description={
          <>
            <span className="text-pushfusion-secondary font-bold">push</span>
            <span className="text-pushfusion font-extrabold">fusion </span>
            provides the tools that you need to understand and manage your
            estate compliance and proactively address any problems before they
            happen.
          </>
        }
        data={[
          {
            icon: (
              <Icon
                className="h-16 w-16 text-blue-50"
                aria-hidden="true"
                color="#0E98FA"
                icon="ui-design"
              />
            ),
            title: "Air quality in one place",
            content: (
              <>
                <p>
                  Track sensors across your estate, broken down by location.
                  Taking the lead from our Emergency Compliance module we
                  believe users should be able to get to the information they
                  need to do their job as quickly as possible. That’s why the
                  Map view shows a single marker icon representing the current
                  risk level for each building that can be filtered to show just
                  the exceptions.
                </p>
                <p>
                  No matter how big your estate is, pushfusion can help you to
                  meet indoor air quality targets and empower you to provide
                  clean air for all your building users.
                </p>
              </>
            ),
            image: (
              <StaticImage
                src="../../assets/images/sensors/pushfusion-sensors-dashboard.png"
                alt="Image of pushfusion interface on a mac book and pushfusion mobile app on an Iphone. Background shows commercial buildings."
                placeholder="blurred"
                layout="constrained"
                quality={100}
                height={446}
                loading="eager"
                aspectRatio={16 / 9}
                backgroundColor="transparent"
                className="h-full rounded-xl shadow-xl"
              />
            ),
          },
          {
            icon: (
              <Icon
                className="h-16 w-16 text-blue-50"
                aria-hidden="true"
                color="#0E98FA"
                icon="analytics8"
              />
            ),
            title: "Turn information into actionable insights",
            content: (
              <>
                <p>
                  Take indoor air quality of your estate to a whole new level
                  with pushfusion ready-made reports or create your own with the
                  custom report builder.
                </p>
                <p>
                  With air quality data, you can get the whole picture about
                  your estate. Use this information to get an understanding of
                  your current air quality and identify areas for improvement.
                  Set up alerts, generate reports, and get tips based on your
                  current air quality situation.
                </p>
              </>
            ),
            image: (
              <StaticImage
                src="../../assets/images/sensors/pushfusion-air-quality-7-days-report.png"
                alt="Image of pushfusion interface on a mac book and pushfusion mobile app on an Iphone. Background shows commercial buildings."
                placeholder="blurred"
                layout="constrained"
                quality={100}
                height={446}
                loading="eager"
                aspectRatio={16 / 9}
                backgroundColor="transparent"
                className="h-full w-full rounded-xl shadow-xl"
              />
            ),
          },
        ]}
      />

      <Cta
        title="Put your building’s health"
        subtitle="under the microscope with pushfusion."
        button1={
          <BookADemoButton
            background="bg-gray-50"
            backgroundHover="bg-gray-300"
            textColor="text-pushfusion"
          />
        }
      />
    </Layout>
  )
}
